import { type Map as MaplibreMap } from "maplibre-gl";

export interface MapImageConfig {
  id: string;
  path: string;
}

const inFlightImageLoads = new WeakMap<MaplibreMap, Map<string, Promise<void>>>();
const imageRegistryPerMap = new WeakMap<MaplibreMap, Map<string, string>>();

function getOrCreateImageRegistry(map: MaplibreMap): Map<string, string> {
  let registry = imageRegistryPerMap.get(map);
  if (!registry) {
    registry = new Map();
    imageRegistryPerMap.set(map, registry);
  }

  return registry;
}

async function loadMapImageOnce(
  map: MaplibreMap,
  imageId: string,
  imagePath: string,
): Promise<void> {
  if (map.hasImage(imageId)) {
    return;
  }

  let mapInFlight = inFlightImageLoads.get(map);
  if (!mapInFlight) {
    mapInFlight = new Map();
    inFlightImageLoads.set(map, mapInFlight);
  }

  const existing = mapInFlight.get(imageId);
  if (existing) {
    await existing;
    return;
  }

  const promise = (async () => {
    try {
      const image = await map.loadImage(imagePath);

      if (!map.hasImage(imageId)) {
        map.addImage(imageId, image.data);
        map.triggerRepaint();
      }
    } catch (error) {
      console.error(`Failed to load image ${imageId} from ${imagePath}:`, error);
      throw error;
    } finally {
      mapInFlight?.delete(imageId);
    }
  })();

  mapInFlight.set(imageId, promise);
  await promise;
}

/**
 * Loads a single image into MapLibre map
 * @param map - MapLibre map instance
 * @param imageId - Unique identifier for the image in the map
 * @param imagePath - URL or path to the image
 * @returns Promise that resolves when image is loaded
 */
export async function loadMapImage(
  map: MaplibreMap,
  imageId: string,
  imagePath: string,
): Promise<void> {
  await loadMapImageOnce(map, imageId, imagePath);
}

/**
 * Loads multiple images into MapLibre map in parallel
 * @param map - MapLibre map instance
 * @param images - Array of image configurations
 * @returns Promise that resolves when all images are loaded
 */
export async function loadMapImages(
  map: MaplibreMap,
  images: MapImageConfig[],
): Promise<void> {
  const missingImages = images.filter((image) => !map.hasImage(image.id));
  if (missingImages.length === 0) {
    map.triggerRepaint();
    return;
  }

  const results = await Promise.allSettled(
    missingImages.map((image) => loadMapImageOnce(map, image.id, image.path)),
  );

  const failures = results
    .map((result, index) => (
      result.status === "rejected"
        ? { image: missingImages[index], reason: result.reason }
        : null
    ))
    .filter((item): item is { image: MapImageConfig; reason: unknown } => item !== null);

  if (failures.length > 0) {
    console.error("Failed to load map images:", failures);
  }

  map.triggerRepaint();
}

/**
 * Registers image id → path mappings for lazy loading via styleimagemissing.
 */
export function registerMapImages(
  map: MaplibreMap,
  images: MapImageConfig[],
): void {
  const registry = getOrCreateImageRegistry(map);

  for (const image of images) {
    registry.set(image.id, image.path);
  }

  map.triggerRepaint();
}

/**
 * Removes image id mappings when a marker source unmounts.
 */
export function unregisterMapImages(
  map: MaplibreMap,
  imageIds: string[],
): void {
  const registry = imageRegistryPerMap.get(map);
  if (!registry) {
    return;
  }

  for (const imageId of imageIds) {
    registry.delete(imageId);
  }
}

/**
 * Subscribes to styleimagemissing and loads registered images on demand.
 */
export function attachStyleImageMissingHandler(map: MaplibreMap): () => void {
  const handler = (event: { id: string }) => {
    if (map.hasImage(event.id)) {
      return;
    }

    const path = imageRegistryPerMap.get(map)?.get(event.id);
    if (!path) {
      return;
    }

    void loadMapImageOnce(map, event.id, path);
  };

  map.on("styleimagemissing", handler);

  return () => {
    map.off("styleimagemissing", handler);
  };
}
